import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { select, Store } from '@ngrx/store';
import { TslDialog, TslFileDrop } from '@tesla/ng-tds';
import { AppSettings } from 'app/app.settings';
import { FileUploadService } from 'app/common/services/fileUpload.service';
import { DocumentType } from 'app/profile/models/onboarding.model';
import { DocumentTemplate, OPERATION, Supplier, SupplierContact, SupplierContactPropertyValue, SupplierDocument, SupplierDocumentPropertyValues, SupplierPropertyValue } from 'app/profile/models/supplier.model';
import { ContactsService } from 'app/profile/services/contacts.service';
import { ESignService } from 'app/profile/services/esign.service';
import { OnboardingDocumentsService } from 'app/profile/services/onboarding-document.service';
import { SupplierDocumentService } from 'app/profile/services/supplier-document.service';
import { GetSupplierContactListByCode } from 'app/profile/store/app.actions';
import { SelectSupplierContactList } from 'app/profile/store/app.selector';
import { SupplierState } from 'app/profile/store/state/supplierstate.state';
import { I18nService } from 'app/shared/services/i18n.service';
import { Contact } from 'app/shared/services/language-mapping';
import { DecrementLoaderCount, IncrementLoaderCount } from 'app/shared/store/actions';
import { getNestedNode, isNullOrEmpty, validateESignDocument } from 'app/shared/utils/utils';
import { stringify } from 'querystring';
import { combineLatest } from 'rxjs';
import { isNullOrUndefined } from 'util';
import * as FileSaver from 'file-saver';
import { ProfileService } from 'app/profile/services/profile.service';

@Component({
  selector: 'app-pcf-dialog',
  templateUrl: './pcf-dialog.component.html',
  styleUrls: ['./pcf-dialog.component.scss']
})
export class PcfDialogComponent implements OnInit {
  @ViewChild('fileDrop', { static: false }) fileDrop: TslFileDrop;

  supplierCode: string;
  isDocumentAdding: boolean = false;
  document: SupplierDocument = null;
  uploadDocument: SupplierDocument = null;
  esignAgreementId: string = null;
  esignAgreementStatus: string = null;
  supplierContacts: SupplierContactPropertyValue[] = [];
  primaryContact: SupplierContactPropertyValue;
  pcfTemplates: DocumentTemplate[] = [];
  primaryPendingForApproval: boolean = false;
  isSupplierRemitToAddressChina: boolean = false;
  documentTypes: DocumentType[] = [];
  constructor(public dialogRef: MatDialogRef<PcfDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<SupplierState>,
    private tslDialog: TslDialog,
    private snackBar: MatSnackBar,
    private translate: I18nService,
    private fileUploadService: FileUploadService,
    private config: AppSettings,
    private contactsService: ContactsService,
    private documentService: SupplierDocumentService,
    private onboardingService: OnboardingDocumentsService,
    private profileService: ProfileService,
    private esignService: ESignService,) {
    if (data) {
      this.supplierCode = data.supplierCode;
    }
  }

  ngOnInit() {
    this.store.dispatch(new IncrementLoaderCount());
    var pcfTemplate = this.documentService.getDocumentTemplates("PCF");
    var docTypes = this.onboardingService.getDocumentTypes();
    var isSupplierRemitToAddressChina = this.profileService.isSupplierRemitToAddressChina(this.supplierCode);
    combineLatest([pcfTemplate, docTypes, isSupplierRemitToAddressChina]).subscribe(([pcf, docType, isSupplierRemitToAddressChina]) => {
      this.pcfTemplates = pcf;
      this.documentTypes = docType;
      this.isSupplierRemitToAddressChina = isSupplierRemitToAddressChina;
      this.store.dispatch(new DecrementLoaderCount());
    })
    this.store.dispatch(new GetSupplierContactListByCode(this.supplierCode, false));
    this.store.pipe(select(SelectSupplierContactList)).subscribe(contacts => {
      this.supplierContacts = contacts;
      let primary = this.supplierContacts.find(x => x.types.newValue.indexOf("Primary") > -1);
      if (primary) {
        this.primaryContact = primary;
        if (this.primaryContact && this.primaryContact.operation && !isNullOrUndefined(this.primaryContact.operation.newValue)) {
          this.primaryPendingForApproval = true;
        }
      } else {
        console.log("No primary contact role");
      }
    })
  }

  onFileAdd(event): void {
    let file = event[0];
    if (file) {
      const attachment = new SupplierDocument();
      attachment.Attachment = file;
      attachment.FileName = file.name;
      this.document = attachment;
      this.isDocumentAdding = false;
    }
  }

  clearFile() {
    this.fileDrop.reset(true);
    this.document = null;
  }

  submitPCF() {
    if (this.primaryContact && !this.primaryPendingForApproval) {
      if (this.document) {
        this.fileUploadService.uploadFileToServer([this.document.Attachment as File], this.supplierCode).subscribe(doc => {
          if (doc) {
            const document = new SupplierDocument();
            document.Operation = OPERATION.CREATE;
            document.DocumentID = doc.documentID;
            document.DocumentType = 'PCF';
            document.FileName = doc.fileName;
            document.DocumentVersion = doc.documentVersion;
            document.ReferenceSystem = 'DMS';
            document.EffectiveDate = new Date();

            this.uploadDocument = document;
            this.submit();
          }
        });
      } else if (!isNullOrUndefined(this.esignAgreementId) && !isNullOrEmpty(this.esignAgreementId)) {
        this.esignService.getESignDocumentStatus(this.esignAgreementId).subscribe(res => {
          this.esignAgreementStatus = res;
          var result = validateESignDocument(res);
          if (result == true) {
            const document = new SupplierDocument();
            document.Operation = OPERATION.CREATE;
            document.DocumentType = 'PCF';
            document.ESignAgreementId = this.esignAgreementId;
            this.uploadDocument = document;
            this.submit();
          } else {
            this.tslDialog.alert('Validation', 'Please upload signed document or complete E-signdocument.');
          }
        });
      }
      else {
        this.tslDialog.alert('Validation', 'Please upload signed document or complete E-signdocument.');
      }
    } else {
      if (this.primaryPendingForApproval) {
        this.tslDialog.alert('Validation', 'Primary contact changes are pending for approval.');
      } else {
        this.tslDialog.alert('Validation', 'No primary contact role.');
      }
    }
  }

  close(): void {
    this.dialogRef.close(true);
  }

  /** Downloads template of a document */
  public downloadPCFTemplate() {
    let uploadTemplate = this.pcfTemplates.filter(x => x.templateType == "upload")[0]
    if (uploadTemplate) {
      this.onboardingService.downloadPCFTemplate(uploadTemplate.template, this.supplierCode, null).subscribe(resultBlob => {
        FileSaver.saveAs(resultBlob, "PCF.pdf");
      });;
    }
  }

  openEsignDocument(agreementId: string) {
    this.esignService.getESignDocumentStatus(agreementId).subscribe(documentStatus => {

      if (validateESignDocument(documentStatus)) {
        this.tslDialog.alert('Validation', 'E-Sign is complete for this document');
      } else {
        this.esignService.getEsignUrl(agreementId).subscribe(urlResp => {
          if (urlResp) {
            window.open(urlResp.esignUrl, "_blank");
          }
        });
      }
    });
  }

  submit() {
    var supplier = new SupplierPropertyValue();
    supplier.contacts = [];
    supplier.contacts.push(this.primaryContact);
    var supplierDetails = this.pickNewValues(supplier);
    if (supplierDetails.Contacts && supplierDetails.Contacts.length > 0) {
      supplierDetails.Contacts.forEach(x => {
        x["documents"] = [];
        x["documents"].push(this.uploadDocument.DocumentID);
      });
    }

    if(this.data.disableSelfSubmit == true){
      this.dialogRef.close([this.supplierCode, supplierDetails.Contacts, [this.uploadDocument]]);
    }else{
      this.contactsService.processContactChange(this.supplierCode, supplierDetails.Contacts, [this.uploadDocument]).subscribe(evtResult => {
        if (evtResult) {
          if (evtResult.messages && evtResult.messages.filter(x => x.status === 'Error').length === 0) {
            // Close the popup and reload contact
            // this.loadData(true);
            this.snackBar.open('Success!', null, {
              duration: 5000,
              verticalPosition: 'top',
              panelClass: ['blue-snackbar']
            });
            this.close();
          } else {
            console.log('Error occurred while posting contact change event.', evtResult.messages);
            this.ShowError('Error', 'Error occurred while changing contact');
          }
        }
        else {
          console.log('Error occurred while posting contact change event.', evtResult.messages);
          this.ShowError('Error', 'Error occurred while posting contact change');
        }
  
      }, err => {
        console.error(`Service call error: processContactChange service call could not be completed. Details: ` + err);
        this.ShowError("Error", JSON.parse(err.Message).error.message);
      },
        () => {
          // TO clear or temporary changes on grid when error occurs
          this.store.dispatch(new GetSupplierContactListByCode(this.supplierCode, true));
        });
    }
  }

  pickNewValues(supplierData: SupplierPropertyValue): Supplier {
    if (!supplierData) {
      return null;
    }
    const supplier = new Supplier();

    if (supplierData.contacts) {
      supplier.Contacts = [];
      supplierData.contacts.forEach((contact, index) => {
        supplier.Contacts[index] = new SupplierContact();
        Object.keys(contact).forEach(name => {
          supplier.Contacts[index][name] = getNestedNode([name, 'newValue'], contact);
        });
        supplier.Contacts[index].Operation = OPERATION.UPDATE;
        supplier.Contacts[index].StageID = null;
      });
    }

    if (this.document) {
      supplier.Documents = [];
      let doc = new SupplierDocument();
      Object.keys(document).forEach(name => {
        doc[name] = getNestedNode([name, 'newValue'], document);
      });
      doc.Operation = OPERATION.CREATE;
    }

    return supplier;
  }

  ShowError(title: string, message: string) {
    this.translate.getAsync(message).subscribe((x: string) => {
      this.snackBar.open(x, '', {
        duration: 5000,
        verticalPosition: 'top',
        panelClass: ['red-snackbar']
      });
    });
  }

  onDocumentAdding() {
    this.isDocumentAdding = true;
  }

  public publishForESign() {
    let typeId = <number><unknown>this.documentTypes.filter(x => x.name == "PCF")[0]?.documentTypeID;
    let templateId = this.pcfTemplates.filter(x => x.templateType == "esign")[0]?.template;
    this.esignService.publishForESign(this.supplierCode, templateId, typeId).mergeMap(resp => {
      this.esignAgreementId = resp.agreementId;
      return this.esignService.getESignDocumentStatus(resp.agreementId);
    }).subscribe(status => {
      this.esignAgreementStatus = status;
      //  Open document to sign
      this.openEsignDocument(this.esignAgreementId);
    });
  }

  get guideLinesUrl(): string {
    return `${this.config.SRM_UI_URL}/documents`;
  }
}
